import React, { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { navigationService } from 'src/config/rootService';
import { getMyProviders } from 'src/modules/providers/selectors/providersSelectors';
import { useProvider } from 'src/modules/providers/hooks/useProvider';
import { ConfirmModal, ConfirmModalProps } from 'src/modules/care/components/ConfirmModal';
import { ProviderType } from 'src/modules/user/models/user';
import { getConfigForProviderType } from 'src/modules/providers/helpers/getConfigForProviderType';

interface Props {
    isVisible: ConfirmModalProps['isVisible'];
    onCancel: ConfirmModalProps['onCancel'];
    onConfirm?: ConfirmModalProps['onConfirm'];
    providerUuid: string;
}

export const ChangeProviderModal: React.FC<Props> = ({
    isVisible,
    onCancel,
    onConfirm,
    providerUuid,
}) => {
    const { t } = useTranslation();
    const { fetchMyProviders } = useProvider();

    const providers = useSelector(getMyProviders);
    const provider = useMemo(
        () => providers.find(p => p.uuid === providerUuid),
        [providerUuid, providers],
    );

    // If there is no provider found and the modal is visible,
    // fetch all providers to attempt to update provider data
    // and find a matching one.
    useEffect(() => {
        if (!provider && isVisible) {
            fetchMyProviders();
        }
    }, [isVisible, provider, fetchMyProviders]);

    if (!provider) {
        return null;
    }

    const getProviderDescription = () =>
        getConfigForProviderType(provider, {
            [ProviderType.THERAPIST]: t('providers:provider_type.therapist').toLowerCase(),
            [ProviderType.COACH]: t('providers:provider_type.coach').toLowerCase(),
            [ProviderType.PSYCHIATRIST]: provider.psychiatristSubtype || '', // translated from the server
        });

    const getProviderSessionType = () =>
        getConfigForProviderType(provider, {
            [ProviderType.THERAPIST]: t('shared:sessionType.therapy'),
            [ProviderType.COACH]: t('shared:sessionType.coaching'),
            [ProviderType.PSYCHIATRIST]: '', // empty for now
        });

    return (
        <ConfirmModal
            isVisible={isVisible}
            onConfirm={() => {
                onConfirm?.();

                if (provider.providerType === ProviderType.THERAPIST) {
                    navigationService.navigate('/work-with-a-therapist', {
                        therapistToRemoveUuid: provider.uuid,
                    });
                    return;
                }

                navigationService.navigate('/work-with-a-coach', {
                    coachToRemoveUuid: provider.uuid,
                });
            }}
            onCancel={onCancel}
            title={t('providers:provider_actions.change.title', {
                providerType: getProviderDescription(),
            })}
            description={t('providers:provider_actions.change.description', {
                providerFullName: `${provider.firstName || ''} ${provider.lastName || ''}`,
                providerType: getProviderDescription(),
                sessionType: getProviderSessionType(),
            })}
            confirmLabel={t('common.continue')}
        />
    );
};
